import { forwardRef, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type MRT_ColumnDef,
  type MRT_Row,
} from 'mantine-react-table';

import '../pages/KanbanColumn.css';
import { Opportunity } from './OpportunityPageTypes';
import {
  buildOpportunityNameMaps,
  formatOpportunityListRow,
  type OpportunityListRow,
} from './OpportunityPageUtilities';
import { Options } from '../utility/types';
import { buildMrtLocalization } from '../i18n/mrtLocale';
import { getStageLabel } from '../i18n/stageLabels';
import { useMrtTableContainerProps, usePageTableShellStyle } from '../utility/mobilePageLayout';
import {
  ExportableMantineReactTable,
  useExportableTable,
} from '../utility/useExportableTable';
import { enableGlobalSearchOnColumns } from '../utility/mrtTableSearch';

export type OpportunityTableViewHandle = {
  getFilteredOpportunities: () => Opportunity[];
  clearFilters: () => void;
};

export const OpportunityTableView = forwardRef<
  OpportunityTableViewHandle,
  {
    opportunities: Opportunity[];
    ownerOptions?: Options[];
    contactOptions?: Options[];
    onClick: (opp: Opportunity) => void;
    onOpenImport: () => void;
  }
>(function OpportunityTableView(
  {
    opportunities,
    ownerOptions = [],
    contactOptions = [],
    onClick,
    onOpenImport,
  },
  ref,
) {
  const { t, i18n } = useTranslation();
  const tableShellStyle = usePageTableShellStyle();
  const mrtTableContainerProps = useMrtTableContainerProps();

  const { ownerNameById, contactNameById } = useMemo(
    () => buildOpportunityNameMaps(ownerOptions, contactOptions),
    [ownerOptions, contactOptions],
  );

  const opportunitiesFormated = useMemo(
    () =>
      opportunities.map((opportunity) =>
        formatOpportunityListRow(opportunity, ownerNameById, contactNameById),
      ),
    [opportunities, ownerNameById, contactNameById],
  );

  const localization = useMemo(() => buildMrtLocalization(t), [t, i18n.language]);

  const columns = useMemo<MRT_ColumnDef<OpportunityListRow>[]>(
    () =>
      enableGlobalSearchOnColumns(
        [
      {
        accessorKey: 'owner_name',
        header: t('opportunity.table.owner'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'client',
        header: t('opportunity.table.client'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'contact_name',
        header: t('opportunity.table.contact'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'name',
        header: t('opportunity.table.opportunity'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'remito',
        header: t('opportunity.table.remito'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'left' } },
      },
      {
        accessorKey: 'value',
        header: t('opportunity.table.value'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'right' } },
        Cell: ({ cell }) => {
          const n = Number.parseFloat(String(cell.getValue() ?? 0));
          return `USD ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
        Footer: ({ table }) => {
          const total = table.getFilteredRowModel().rows.reduce(
            (sum, row) => sum + (Number.parseFloat(String(row.original.value)) || 0),
            0,
          );
          return (
            <span style={{ fontWeight: 700 }}>
              USD {total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          );
        },
      },
      {
        accessorKey: 'discount',
        header: t('opportunity.table.discount'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
      {
        accessorKey: 'current_stage',
        header: t('opportunity.table.stage'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
        Cell: ({ cell }) => getStageLabel(cell.getValue<string>() ?? '', t),
      },
      {
        accessorKey: 'total_duration',
        header: t('opportunity.table.totalTimeDays'),
        mantineTableHeadCellProps: { sx: { backgroundColor: 'rgba(48,113,91,0.2)', color: '#fff' } },
        mantineTableBodyCellProps: { sx: { textAlign: 'center' } },
      },
        ],
        ['name', 'remito'],
      ),
    [t, i18n.language],
  );

  const { table, exportModal } = useExportableTable(
    {
      columns,
      data: opportunitiesFormated,
      localization,
      defaultColumn: { size: 40, minSize: 30, maxSize: 600 },
      enableColumnFilters: true,
      enableSorting: true,
      enableFullScreenToggle: true,
      enableGrouping: false,
      enableColumnActions: false,
      enablePagination: true,
      enableBottomToolbar: true,
      enableRowVirtualization: false,
      enableGlobalFilter: true,
      initialState: {
        showGlobalFilter: true,
        pagination: {
          pageSize: 20,
          pageIndex: 0,
        },
      },
      mantineTableContainerProps: mrtTableContainerProps,
      mantineTableProps: {
        sx: {
          display: 'table',
          tableLayout: 'auto',
          borderCollapse: 'separate',
          '& .mantine-TableHeadCell-Content': {
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1px 1px',
          },
          '& .mantine-TextInput-input': {
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            borderRadius: '4px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#000',
          },
          '& th, & td': {
            borderRadius: '4px',
          },
          '& thead': {
            position: 'sticky',
            top: 0,
            zIndex: 100,
            backgroundColor: 'rgba(48, 113, 91, 0.9)',
            color: '#fff',
          },
        },
      },
      mantineTableBodyRowProps: ({ row }: { row: MRT_Row<OpportunityListRow> }) => ({
        onDoubleClick: () => onClick(row.original),
        style: { cursor: 'pointer' },
      }),
    },
    {
      idField: 'id',
      filenamePrefix: 'opportunities',
      sheetName: 'Opportunities',
      onOpenImport,
      getDeletedValue: (row) => Boolean(row.deletedAt),
      getCellValue: (row, columnId) => {
        if (columnId === 'value') {
          return Number.parseFloat(String(row.value ?? 0)) || 0;
        }
        if (columnId === 'current_stage') {
          return getStageLabel(String(row.current_stage ?? ''), t);
        }
        return row[columnId as keyof typeof row];
      },
    },
  );

  useImperativeHandle(
    ref,
    () => ({
      getFilteredOpportunities: () =>
        table.getPrePaginationRowModel().rows.map((row) => row.original),
      clearFilters: () => { table.setGlobalFilter(''); table.setColumnFilters([]); },
    }),
    [table],
  );

  return (
    <div style={tableShellStyle}>
      <ExportableMantineReactTable table={table} exportModal={exportModal} />
    </div>
  );
});
